import { DEPLOYMENT_TIER, NOODLE_CREATOR_ID, NOODLE_CREATOR_PERSON_ID } from '@configuration/client';

export type FEATURE_FLAG_KEY =
  | 'can-edit-form-request-provider'
  | 'can-show-pay-organizer-table'
  | 'can-edit-noodle-application-fees'
  | 'can-see-activity-thread'
  | 'can-see-customer-view-in-dashboard'
  | 'can-trigger-step'
  | 'confido-cart'
  | 'confido-payment-plans'
  | 'dashboard-pwa-installable'
  | 'dont-hide-uploaded-docs'
  | 'for-testing-feature-flags'
  | 'new-cart'
  | 'uscis'
  | 'integrations'
  | 'can-use-inbox'
  | 'user-profile-chat'
  | 'user-profile-v3'
  | 'workflow-conversation'
  | 'workflow-nodes'
  | 'workflow-template-modal'
  | 'create-workflow-template'
  | 'can-use-ai-reader'
  | 'team-member-availability'
  | 'enable-translations'
  | 'cases-filed-demo'
  | 'can-archive-workflow-template'
  | 'can-use-noodle-questionnaire'
  | 'table-view'
  | 'invoice-pre-payment'
  | 'is-noodle-creator'
  | 'questionnaire-editable-table-view'
  | 'force-customer-debit'
  | 'form-request-ai-summary'
  | 'enable-uat-credit-report'
  | 'is-glade-dot-ai'
  | 'show-payment-plans-tab'
  | 'can-trigger-ai-form-requests'
  | 'update-questionnaire-version'
  | 'pdf-fill-insights'
  | 'rx-db-questionnaire-template-while-answering-cache'
  | 'rx-db-questionnaire-template-while-answering-full'
  | 'questionnaire-show-external-data'
  | 'show-new-creator-ui'
  | 'pacer'
  | 'beta-performance-improvements'
  | 'can-use-reset-autofill'
  | 'can-upgrade-glade-questionnaires';

type Profile = { id: string; email?: string | null } | null;
export const isNoodler = (profile: Profile): boolean => (
  Boolean(profile?.email && /@noodle\.shop/.test(profile.email))
  || Boolean(profile?.email && /@glade\.ai/.test(profile.email))
);

const enabledByEmail = (...emails: string[]) => (profile: Profile): boolean => (
  Boolean(profile?.email && emails.includes(profile.email))
);

const enabledByCreator = (...creatorIds: string[]) => (creatorId?: string | null): boolean => Boolean(creatorId && creatorIds.includes(creatorId));

const isNoodleCreator = (creatorId?: string | null): boolean => Boolean(
  creatorId && (creatorId === NOODLE_CREATOR_ID),
);

const isGladeAdmin = (profile: Profile): boolean => profile?.id === NOODLE_CREATOR_PERSON_ID;

const isProduction = (): boolean => DEPLOYMENT_TIER === 'production';

const checkFeatureFlag = (featureFlagKey: FEATURE_FLAG_KEY, profile: Profile, creatorId?: string | null): boolean => {
  if (featureFlagKey === 'for-testing-feature-flags') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'dashboard-pwa-installable') {
    return true;
  }

  if (featureFlagKey === 'workflow-conversation') {
    return false;
  }

  if (featureFlagKey === 'can-show-pay-organizer-table') {
    return true;
  }

  if (featureFlagKey === 'workflow-nodes') {
    return !isProduction();
  }

  if (featureFlagKey === 'integrations') {
    return true;
  }

  if (featureFlagKey === 'user-profile-v3') {
    return true;
  }

  if (featureFlagKey === 'user-profile-chat') {
    return true;
  }

  if (featureFlagKey === 'can-use-inbox') {
    return true;
  }

  if (featureFlagKey === 'workflow-template-modal') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'can-trigger-step') {
    return true;
  }

  if (featureFlagKey === 'is-glade-dot-ai') {
    return true;
  }

  if (featureFlagKey === 'questionnaire-show-external-data') {
    return true;
  }

  if (featureFlagKey === 'can-edit-form-request-provider') {
    return !isProduction() || enabledByEmail('accounts@noodle.shop', 'accounts@glade.ai', 'pradeek@compleximmigration.com', 'sid.horse+test@gmail.com')(profile);
  }

  if (featureFlagKey === 'uscis') {
    return !isProduction() || enabledByCreator('clh88zh9n07oa0bmyudap1ujz')(creatorId);
  }

  if (featureFlagKey === 'can-see-activity-thread') {
    return true;
  }

  if (featureFlagKey === 'can-see-customer-view-in-dashboard') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'create-workflow-template') {
    return enabledByEmail('accounts@noodle.shop', 'accounts@glade.ai')(profile);
  }

  if (featureFlagKey === 'confido-cart') {
    return true;
  }

  if (featureFlagKey === 'confido-payment-plans') {
    return true;
  }

  if (featureFlagKey === 'can-edit-noodle-application-fees') {
    return !isProduction() || enabledByEmail('accounts@noodle.shop', 'accounts@glade.ai')(profile);
  }

  if (featureFlagKey === 'can-use-ai-reader') {
    /* temporarily disabling as per dev-3619, may need to remove this completely in future */
    // return isNoodler(profile);
    return false;
  }

  if (featureFlagKey === 'team-member-availability') {
    return true;
  }

  if (featureFlagKey === 'enable-translations') {
    return true;
  }

  if (featureFlagKey === 'can-archive-workflow-template') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'cases-filed-demo') {
    return true;
  }

  if (featureFlagKey === 'can-use-noodle-questionnaire') {
    return isNoodler(profile);
  }

  if (featureFlagKey === 'table-view') {
    return true;
  }

  if (featureFlagKey === 'invoice-pre-payment') {
    return true;
  }

  if (featureFlagKey === 'is-noodle-creator') {
    return isNoodleCreator(creatorId);
  }

  if (featureFlagKey === 'force-customer-debit') {
    return true;
  }

  if (featureFlagKey === 'questionnaire-editable-table-view') {
    return false;
  }

  if (featureFlagKey === 'form-request-ai-summary') {
    return true;
  }

  if (featureFlagKey === 'show-payment-plans-tab') {
    return true;
  }

  if (featureFlagKey === 'can-trigger-ai-form-requests') {
    return true;
  }

  if (featureFlagKey === 'update-questionnaire-version') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'pdf-fill-insights') {
    return !isProduction() || isNoodler(profile);
  }

  if (featureFlagKey === 'enable-uat-credit-report') {
    return true;
  }

  if (featureFlagKey === 'show-new-creator-ui') {
    return true;
  }

  if (featureFlagKey === 'pacer') {
    return !isProduction() || enabledByEmail('accounts@glade.ai')(profile);
  }

  if (featureFlagKey === 'rx-db-questionnaire-template-while-answering-cache') {
    return true;
  }

  if (featureFlagKey === 'rx-db-questionnaire-template-while-answering-full') {
    return true;
  }

  if (featureFlagKey === 'beta-performance-improvements') {
    return true;
  }

  if (featureFlagKey === 'can-use-reset-autofill') {
    return true;
  }

  if (featureFlagKey === 'can-upgrade-glade-questionnaires') {
    return isGladeAdmin(profile);
  }

  return false;
};

export default checkFeatureFlag;
